import React, { useState, useEffect } from "react";
import { Box, Typography, Divider, useTheme, useMediaQuery } from "@mui/material";
import SearchBar from "../components/SearchBar";
import QuickAddList from "../components/QuickAddList";
import OrderListSell from "../components/OrderListSell";
import { fetchProducts } from "../api/superAdmin";
import { fetchQuickAddProducts, addQuickAddProduct, removeQuickAddProduct } from "../api/quickAddapi";
import { Product, Variant, OrderItem } from "../types";
import { formatAttributes as fmtAttrs } from "../utils/variantAttributes";


const SellPage: React.FC = () => {

  const [products, setProducts] = useState<Product[]>([]);
  const [quickAddProducts, setQuickAddProducts] = useState<Product[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>(() => {
    const saved = localStorage.getItem('posCart');
    return saved ? JSON.parse(saved) : [];
  });
  
  useEffect(() => {
    localStorage.setItem('posCart', JSON.stringify(orderItems));
  }, [orderItems]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const fetchedProducts = await fetchProducts();
        setProducts(fetchedProducts);
      } catch (error) {
        console.error("Error loading products:", error);
      }
    };

    const loadQuickAddProducts = async () => {
      try {
        const fetchedQuickAddProducts = await fetchQuickAddProducts();
        setQuickAddProducts(fetchedQuickAddProducts);
      } catch (error) {
        console.error("Error loading Quick Add products:", error);
      }
    };

    loadProducts();
    loadQuickAddProducts();
  }, []);

  const handleAddToQuickAdd = async (product: Product) => {
    if (!quickAddProducts.some((item) => item.product_id === product.product_id)) {
      try {
        await addQuickAddProduct(product.product_id);
        setQuickAddProducts((prev) => [...prev, product]);
      } catch (error) {
        console.error("Error adding to Quick Add:", error);
      }
    }
  };

  const handleRemoveFromQuickAdd = async (productId: number) => {
    try {
      await removeQuickAddProduct(productId);
      setQuickAddProducts((prev) =>
        prev.filter((product) => product.product_id !== productId)
      );
    } catch (error) {
      console.error("Error removing from Quick Add:", error);
    }
  };


  const handleAddToOrder = (variant: Variant) => {
    const existingItemIndex = orderItems.findIndex((item) => item.variant_id === variant.id);
    const attributeSummary = fmtAttrs(variant.attributes, { sep: " / ", empty: "" });


  

    if (existingItemIndex > -1) {
      const updatedOrder = [...orderItems];
      updatedOrder[existingItemIndex].quantity += 1;
      setOrderItems(updatedOrder);
    } else {
      setOrderItems((prev) => [
        ...prev,
        {
          product_id: variant.product_id,
          variant_id: variant.id,
          name: attributeSummary || variant.sku || "Variant",
          price: variant.price,
          quantity: 1,
          image_url: variant.image_url || undefined, // Pass the variant's image URL
          attributes: variant.attributes, // 🔥 Add this!

        },
      ]);
    }
  };

  const handleRemoveFromOrder = (variantId: number) => {
    setOrderItems((prev) => prev.filter((item) => item.variant_id !== variantId));
  };

  const handleClearOrder = () => {
    setOrderItems([]); // Clear the order items after payment
  };
  const handleUpdateQuantity = (variantId: number, quantity: number) => {
    setOrderItems((prevItems) =>
      prevItems.map((item) =>
        item.variant_id === variantId ? { ...item, quantity } : item
      )
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: "100vh",
        gap: 2,
        p: 2,
      }}
    >
      <Box sx={{ flex: isMobile ? "none" : 2, mb: isMobile ? 2 : 0 }}>
        <Typography variant="h6">Sell Products</Typography>
        <Divider sx={{ my: 2 }} />
        <SearchBar
          products={products}
          onAddToOrder={handleAddToOrder}
          onAddToQuickAdd={handleAddToQuickAdd}
        />
        <Divider sx={{ my: 2 }} />
        <QuickAddList
          quickAddProducts={quickAddProducts}
          onRemoveFromQuickAdd={handleRemoveFromQuickAdd}
          onAddToOrder={handleAddToOrder}
        />
      </Box>

      <Box
        sx={{
          flex: isMobile ? "none" : 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          p: 2,
          boxShadow: theme.palette.mode === "light" ? "0px 4px 6px rgba(0, 0, 0, 0.1)" : "none",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Order Summary
        </Typography>
        <Divider
          sx={{
            mb: 2,
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.divider
                : "rgba(255, 255, 255, 0.12)",
          }}
        />
        <OrderListSell
  orderItems={orderItems}
  onRemoveFromOrder={handleRemoveFromOrder}
  onClearOrder={handleClearOrder}
  onUpdateQuantity={handleUpdateQuantity} // Add this prop
/>
      </Box>
    </Box>
  );
};

export default SellPage;
